<template>
  <div class="header-container">
    <div class="header-wrapper">
      <div class="logo">
        <img src="/logo-black.png" alt="Nespresso Logo" class="logo-image" />
      </div>

      <div class="search-container" ref="searchContainer">
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="Поиск продуктов..."
          @input="handleSearch"
          @focus="isSearchFocused = true"
        />
        <div
          v-if="isSearchFocused && searchQuery && searchResults.length"
          class="search-results"
        >
          <div
            v-for="result in searchResults"
            :key="result.id"
            class="search-result-item"
            @click="handleResultClick(result)"
          >
            <img :src="result.imgUrl" :alt="result.name" class="result-image" />
            <div class="result-info">
              <div class="result-name">{{ result.name }}</div>
              <div class="result-details">
                <span class="result-collection">{{ result.collection }}</span>
                <span class="result-price">{{ result.price }}₽</span>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="isSearchFocused && searchQuery && !searchResults.length"
          class="no-results"
        >
          <p>Товары не найдены</p>
        </div>
      </div>
    </div>

    <nav class="nav-wrapper">
      <div
        class="nav-item"
        :class="{ active: currentType === 'original' }"
        @click="handleTypeChange('original')"
      >
        <img src="/icons/original.svg" alt="Original" class="nav-icon" />
        <span class="nav-label">Original</span>
      </div>
      <div
        v-if="hasVertuo"
        class="nav-item"
        :class="{ active: currentType === 'vertuo' }"
        @click="handleTypeChange('vertuo')"
      >
        <img src="/icons/vertuo.svg" alt="Vertuo" class="nav-icon" />
        <span class="nav-label">Vertuo</span>
      </div>
    </nav>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';

export default {
  name: 'MainHeader',
  props: {
    currentType: {
      type: String,
      default: 'original'
    },
    catalogData: {
      type: Object,
      default: () => ({ original: [], vertuo: [] })
    }
  },
  emits: ['type-change', 'product-search'],
  setup(props, { emit }) {
    const searchQuery = ref('');
    const searchResults = ref([]);
    const isSearchFocused = ref(false);
    const searchContainer = ref(null);

    // Only show the Vertuo tab when there's Vertuo data to show.
    const hasVertuo = computed(() => (props.catalogData.vertuo || []).length > 0);

    const handleTypeChange = (type) => {
      emit('type-change', type);
    };

    const handleSearch = () => {
      if (searchQuery.value.length < 2) {
        searchResults.value = [];
        return;
      }

      const query = searchQuery.value.toLowerCase();
      const allProducts = [];

      // Search through both catalogs
      ['original', 'vertuo'].forEach(catalogType => {
        const catalog = props.catalogData[catalogType] || [];
        catalog.forEach(collection => {
          if (collection.items) {
            collection.items.forEach(item => {
              if (
                item.name.toLowerCase().includes(query) ||
                item.description?.toLowerCase().includes(query) ||
                collection.name.toLowerCase().includes(query)
              ) {
                allProducts.push({
                  ...item,
                  catalogType,
                  collection: collection.name
                });
              }
            });
          }
        });
      });

      searchResults.value = allProducts.slice(0, 5); // Limit to 5 results
    };

    const handleResultClick = (product) => {
      // Close search
      searchQuery.value = '';
      searchResults.value = [];
      isSearchFocused.value = false;

      // Emit event to parent to handle navigation
      emit('product-search', product);
    };

    const handleClickOutside = (event) => {
      if (searchContainer.value && !searchContainer.value.contains(event.target)) {
        isSearchFocused.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      searchQuery,
      searchResults,
      isSearchFocused,
      searchContainer,
      hasVertuo,
      handleTypeChange,
      handleSearch,
      handleResultClick
    };
  }
};
</script>

<style scoped>
.header-container {
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
}

.header-wrapper {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding: 1rem 2rem;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.logo {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.logo-image {
  height: 35px;
  width: auto;
}

.search-container {
  position: relative;
  flex: 1;
  max-width: 500px;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #E5D5BB;
  border-radius: 25px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.3s ease;
  font-family: inherit;
}

.search-input:focus {
  border-color: #876C43;
}

.search-results {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
}

.search-result-item {
  display: flex;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  transition: background 0.2s ease;
  gap: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-item:hover {
  background: #FAF9F8;
}

.result-image {
  width: 50px;
  height: 50px;
  object-fit: contain;
  border-radius: 8px;
  background: #F0EDE5;
  padding: 5px;
}

.result-info {
  flex: 1;
}

.result-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  font-size: 14px;
}

.result-details {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
}

.result-collection {
  color: #876C43;
  font-weight: 500;
}

.result-price {
  font-weight: 600;
  color: #333;
}

.no-results {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 1rem;
  text-align: center;
  color: #666;
  z-index: 1000;
}

.no-results p {
  margin: 0;
  font-size: 14px;
}

.nav-wrapper {
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 100%;
  background-color: #FAF9F8;
  box-shadow: inset 0 4px 6px -4px rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid #E5D5BB;
}

.nav-item {
  height: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  padding: 0.4rem 0;
  text-decoration: none;
  transition: background-color 0.5s;
}

.nav-item:hover {
  background-color: #E2DEDE;
}

.nav-item.active {
  background-color: #E2DEDE;
}

.nav-icon {
  width: 40px;
  height: 40px;
  margin-bottom: 5px;
}

.nav-label {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

@media (max-width: 768px) {
  .header-wrapper {
    padding: 0.8rem 1rem;
    flex-direction: column;
    gap: 1rem;
  }

  .logo-image {
    height: 28px;
  }

  .search-container {
    max-width: 100%;
    width: 100%;
  }

  .search-input {
    font-size: 13px;
    padding: 0.6rem 0.8rem;
  }

  .nav-icon {
    width: 35px;
    height: 35px;
  }

  .nav-label {
    font-size: 12px;
  }

  .result-image {
    width: 40px;
    height: 40px;
  }

  .result-name {
    font-size: 13px;
  }
}
</style>
